import classNames from 'classnames';
import { useRef, useMemo } from 'react';

import { ErrorBoundary, GenericFallback } from '@/lib/error';
import { useResizeHandle } from '@/lib/hooks/use-resize-handle';
import { Breakpoint, useScreenSize } from '@/lib/hooks';
import { ConversationExplorationContextProvider } from '@/explore/conversation/conversation-exploration-context';
import { useConversationRepositoryContext } from '@/explore/conversation/conversation-repository-context';

import { useExplorationUIContext } from '../exploration-ui-context';
import { ExplorationCellContextProvider } from '../exploration-cell-context';
import { CellSidebar } from './cell-sidebar';
import { ExplorationSidebar } from './exploration-sidebar';

import styles, { sidebarMinWidth, sidebarMaxWidth } from './sidebar.module.scss';

const SidebarMinWidth = parseInt(sidebarMinWidth);
const SidebarMaxWidth = parseInt(sidebarMaxWidth);

interface SidebarProps {
  collapsed: boolean;
  onAddSection: () => void;
}
export const Sidebar = ({ onAddSection, collapsed }: SidebarProps) => {
  const { selectedCell } = useExplorationUIContext();
  const { getSelectedConversation } = useConversationRepositoryContext();
  const screenSize = useScreenSize();
  const desktopView = screenSize.breakpoint > Breakpoint.md;

  const resizeHandleRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const maxSize = useMemo(
    () => Math.min(screenSize.width * 0.5, SidebarMaxWidth),
    [screenSize.width],
  );

  const { sidebarWidth, isResizing } = useResizeHandle<HTMLDivElement, HTMLDivElement>(
    resizeHandleRef,
    sidebarRef,
    (target) => target.offsetWidth,
    (size, { dx }) => size - dx,
    (target, size) => (target.style.width = `${size}px`),
    { minSize: SidebarMinWidth, maxSize },
  );

  const selectedConversation = getSelectedConversation(selectedCell?.id ?? '');

  return (
    <div
      ref={sidebarRef}
      className={classNames(styles.sidebarContainer, {
        [styles.collapsed]: collapsed,
        [styles.reducedMotion]: isResizing,
      })}
      style={{ width: sidebarWidth !== null ? `${sidebarWidth}px` : undefined }}>
      {!collapsed && (
        <>
          <div className={styles.resizeHandle} ref={resizeHandleRef} />
          <div className={styles.sidebar}>
            <ErrorBoundary fallback={(errorData) => <GenericFallback {...errorData} />}>
              {selectedCell !== null ? (
                <ConversationExplorationContextProvider
                  conversationId={selectedConversation?.conversationId}>
                  <ExplorationCellContextProvider
                    cell={selectedCell}
                    enforcedRowCollapse={desktopView}
                    isCollapsible>
                    <CellSidebar />
                  </ExplorationCellContextProvider>
                </ConversationExplorationContextProvider>
              ) : (
                <ExplorationSidebar onAddSection={onAddSection} />
              )}
            </ErrorBoundary>
          </div>
        </>
      )}
    </div>
  );
};
